// PATH: src/components/documents/ChapterLessonsTab.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Collapse,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import type { LessonObjective, LessonsReport } from "../../api/objectives/objectives";
import { StatusPill } from "../ui";
import {
  buildLessonRows,
  countLabel,
  formatLessonsAttention,
  formatLessonsSummary,
  summaryTone,
  type LessonRow,
} from "./lessonCheck";

interface Props {
  report: LessonsReport;
  /** Ouvre la base de faits d'un objectif. */
  onOpenObjective: (objectiveId: string, text: string) => void;
}

const ObjectiveLine: React.FC<{ objective: LessonObjective; onOpen: Props["onOpenObjective"] }> = ({
  objective: o,
  onOpen,
}) => (
  <ButtonBase
    onClick={() => onOpen(o.id, o.text)}
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      textAlign: "start",
      gap: 1.5,
      px: 2,
      py: 1,
      width: "100%",
      "&:hover": { bgcolor: "action.hover" },
    }}
  >
    <Typography variant="body2" color="text.secondary" className="mono" sx={{ minWidth: 24, textAlign: "right", pt: 0.25 }}>
      {o.ordinal}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" dir="auto">
        {o.text}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        {o.language && (
          <StatusPill tone="neutral" dot={false}>
            {o.language}
          </StatusPill>
        )}
        {o.kind && (
          <StatusPill tone="info" dot={false}>
            {o.kind}
          </StatusPill>
        )}
        {o.has_facts ? (
          <StatusPill tone="success" title="Faits actifs de la base de faits">
            {countLabel(o.facts_count, "fait", "faits")}
          </StatusPill>
        ) : (
          <StatusPill tone="warning" title="Pas de base de faits : l'élève n'aura qu'un QCM">
            sans faits
          </StatusPill>
        )}
      </Stack>
    </Box>
    <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary", mt: 0.25 }} />
  </ButtonBase>
);

const LessonCard: React.FC<{
  lesson: LessonRow;
  open: boolean;
  onToggle: () => void;
  onOpenObjective: Props["onOpenObjective"];
}> = ({ lesson, open, onToggle, onOpenObjective }) => (
  <Paper variant="outlined" sx={{ overflow: "hidden" }}>
    <ButtonBase
      onClick={onToggle}
      aria-expanded={open}
      sx={{
        display: "flex",
        width: "100%",
        textAlign: "start",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: 1.5,
        px: 2,
        py: 1.25,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Typography variant="body2" className="mono" color="text.secondary" sx={{ minWidth: 24, textAlign: "right", pt: 0.25 }}>
        {lesson.ordre}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" dir="auto" sx={{ wordBreak: "break-word" }}>
          {lesson.titre || lesson.leconRef}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          <StatusPill tone={lesson.tone}>{lesson.label}</StatusPill>
          <Typography variant="caption" color={lesson.status === "ready" ? "text.secondary" : "text.primary"}>
            {lesson.reason}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {countLabel(lesson.passages, "passage", "passages")}
          </Typography>
          <Typography variant="caption" className="mono" color="text.secondary">
            {lesson.leconRef}
          </Typography>
        </Stack>
      </Box>
      <ExpandMoreIcon
        fontSize="small"
        sx={{ color: "text.secondary", mt: 0.25, transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }}
      />
    </ButtonBase>
    <Collapse in={open} unmountOnExit>
      <Divider />
      {lesson.objectives.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
          Aucun objectif n'a été déduit de cette leçon. Une ré-extraction (onglet « Objectifs et faits ») peut la
          combler.
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {lesson.objectives.map((o) => (
            <ObjectiveLine key={o.id} objective={o} onOpen={onOpenObjective} />
          ))}
        </Stack>
      )}
    </Collapse>
  </Paper>
);

/**
 * Onglet « Leçons » : le chapitre leçon par leçon, dans l'ordre du manifeste.
 * Chaque leçon dit son statut (prête / faible / vide) et pourquoi ; dépliée,
 * elle montre ses objectifs, et un clic ouvre la base de faits. Les leçons à
 * reprendre sont dépliées d'emblée.
 */
export const ChapterLessonsTab: React.FC<Props> = ({ report, onOpenObjective }) => {
  const rows = React.useMemo(() => buildLessonRows(report), [report]);
  const [open, setOpen] = React.useState<Set<string>>(
    () => new Set(rows.filter((r) => r.status !== "ready").map((r) => r.leconRef)),
  );
  const s = report.summary;
  const attention = formatLessonsAttention(s);
  const allOpen = rows.length > 0 && rows.every((r) => open.has(r.leconRef));

  const toggle = (ref: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });

  return (
    <Stack spacing={1.5}>
      <Alert severity={summaryTone(s)}>
        <Typography variant="body2" fontWeight={600}>
          {formatLessonsSummary(s)}
        </Typography>
        <Typography variant="body2">
          {attention
            ? `${attention} : les objectifs sont déduits du cours, leçon par leçon ; une leçon est prête avec au moins 2 objectifs, tous avec faits.`
            : "Chaque leçon a au moins 2 objectifs, tous avec une base de faits."}
        </Typography>
        {s.preamble_passages > 0 && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {countLabel(s.preamble_passages, "passage", "passages")} avant la première leçon : sans objectif, c'est
            attendu.
          </Typography>
        )}
      </Alert>

      {rows.length > 1 && (
        <Stack direction="row" justifyContent="flex-end">
          <Button
            size="small"
            onClick={() => setOpen(allOpen ? new Set() : new Set(rows.map((r) => r.leconRef)))}
          >
            {allOpen ? "Tout replier" : "Tout déplier"}
          </Button>
        </Stack>
      )}

      {rows.map((lesson) => (
        <LessonCard
          key={lesson.leconRef}
          lesson={lesson}
          open={open.has(lesson.leconRef)}
          onToggle={() => toggle(lesson.leconRef)}
          onOpenObjective={onOpenObjective}
        />
      ))}

      {(report.unassigned ?? []).length > 0 && (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="subtitle2">
              Sans leçon ({report.unassigned.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Objectifs rattachés à aucune leçon du manifeste (extraction ancienne, ou leçon disparue) : une
              ré-extraction les range.
            </Typography>
          </Box>
          <Divider />
          <Stack divider={<Divider flexItem />}>
            {report.unassigned.map((o) => (
              <ObjectiveLine key={o.id} objective={o} onOpen={onOpenObjective} />
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default ChapterLessonsTab;
